import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetPrinters, dataSetPrintServers } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IPrinter, Printer } from '../../../@types/model/masterData/printer/printer';
import PrinterHttpService from '../../../services/http/masterData/printerHttpService';
import PrintServerHttpService from '../../../services/http/masterData/printServerHttpService';
import { IPrinterFormValues, PrinterFormValues } from '../../../@types/model/masterData/printer/printerFormValues';
import PrinterForm from './form/PrinterForm';
import { IApiError } from '../../../@types/other';
import { setPrinterMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const PrinterScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const printers = useAppSelector(state => state.masterData.printers);
    const printServers = useAppSelector(state => state.masterData.printServers);
    const auth = useAppSelector(state => state.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedPrinter, setSelectedPrinter] = useState<IPrinter>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingPrinter, setDeletingPrinter] = useState<IPrinter>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const [printerRes, printServerRes] = await Promise.all([
                        PrinterHttpService.getPrinterData(),
                        PrintServerHttpService.getPrintServerData(),
                    ]);
                    dispatch(dataSetPrintServers(printServerRes.data));
                    dispatch(dataSetPrinters(printerRes.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading printers.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedPrinter(undefined);
        setFormGuid(undefined);
    }, []);

    const editPrinter = useCallback((printer : IPrinter) => {
        setSelectedPrinter(printer);
        setFormGuid(printer.guid);
        setIsDialogOpen(true);
    }, []);

    const onSubmit = async (values : IPrinterFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const printerPayload = Printer(payload);
            const res = await PrinterHttpService.addOrUpdatePrinter(printerPayload);
            const updatedList = upsertArrayElement(printers, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPrinters(updatedList));
            await setPrinterMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedPrinter ? 'Printer updated successfully.' : 'Printer added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating printer data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = useCallback((printer : IPrinter) => {
        setDeletingPrinter(printer);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingPrinter(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removePrinter = async () => {
        if (!deletingPrinter) return;
        const updatedPrinter = { ...deletingPrinter, isActive: false };
        setIsLoading(true);
        try {
            const res = await PrinterHttpService.deletePrinter(updatedPrinter.id);
            const updatedList = upsertArrayElement(printers, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPrinters(updatedList));
            await setPrinterMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Printer deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting printer.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const getPrintServerName = useCallback((printServerId : number) => {
        const printServer = printServers?.find(x => x.id === printServerId);
        return printServer?.name ?? '';
    }, [printServers]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return PrinterFormValues(selectedPrinter, printServers);
        }
    }, [selectedPrinter, printServers, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth?.session?.user?.rights, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Printer'
                    isEdit={!!selectedPrinter}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <PrinterForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IPrinter>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editPrinter}
                        enableEditing={hasEditingRight}
                        enableDeleting={printer => printer.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Default Paper', field: 'defaultPaper', enableFiltering: true, enableSorting: true },
                            { title: 'IP Address', field: 'ipAddress', enableFiltering: true, enableSorting: true },
                            { title: 'Print Server', field: 'printServerId', formatFunction: getPrintServerName, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={printers}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Printer'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this printer?'
                onAccept={removePrinter}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default PrinterScreen;
